<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';
import Banner from 'next/banner/Banner.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import WhatsappChannel from 'dashboard/api/channel/whatsappChannel';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import globalConstants from 'dashboard/constants/globals.js';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
  isValidBusinessData,
  isEmbeddedSignupFinishEvent,
  extractEmbeddedSignupError,
} from './whatsapp/utils';

const props = defineProps({
  enableCallingOnComplete: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  showRestrictionAlert: {
    type: Boolean,
    default: false,
  },
  restrictionStatusUrl: {
    type: String,
    default: '',
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

// State
const isProcessing = ref(false);
const processingMessage = ref('');
const isAuthenticating = ref(false);
const authCode = ref(null);
const authCodeReceived = ref(false);
const businessData = ref(null);
const businessFlowEvent = ref('FINISH');
const isCoexistenceEnabled = ref(false);
const deferAutoFillSetup = ref(true);
const enableCoexistenceLogsNow = ref(false);
const coexistenceSyncMode = ref('contacts_and_history');
const acknowledgeHistoryWindow = ref(false);
const acknowledgeChunkedHistory = ref(false);
const acknowledgeBsuidIdentity = ref(false);
const acknowledgeIdentityChangeFlow = ref(false);
const existingNumbers = ref([]);
const isLoadingExistingNumbers = ref(false);
const importingPhoneNumberId = ref('');
const manualWabaId = ref('');
const manualPhoneNumberId = ref('');

const META_APP_ID = window.chatwootConfig?.whatsappAppId || '1466069688257587';
const META_API_VERSION = window.chatwootConfig?.whatsappApiVersion || 'v25.0';
const META_CONFIGURATION_ID = window.chatwootConfig?.whatsappConfigurationId;

const benefits = computed(() => [
  {
    key: 'EASY_SETUP',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.EASY_SETUP'),
  },
  {
    key: 'SECURE_AUTH',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.SECURE_AUTH'),
  },
  {
    key: 'AUTO_CONFIG',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.AUTO_CONFIG'),
  },
]);

const showLoader = computed(() => isAuthenticating.value || isProcessing.value);
const currentUser = computed(() => store.getters.getCurrentUser);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const isImportingExistingNumber = computed(
  () => importingPhoneNumberId.value !== ''
);
const coexistenceChecks = computed(() => [
  {
    key: 'history_window',
    enabled: acknowledgeHistoryWindow.value,
    label: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.HISTORY_WINDOW_LABEL'
    ),
  },
  {
    key: 'chunked_history',
    enabled: acknowledgeChunkedHistory.value,
    label: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.CHUNKED_HISTORY_LABEL'
    ),
  },
  {
    key: 'bsuid_identity',
    enabled: acknowledgeBsuidIdentity.value,
    label: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.BSUID_IDENTITY_LABEL'
    ),
  },
  {
    key: 'identity_change',
    enabled: acknowledgeIdentityChangeFlow.value,
    label: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.IDENTITY_CHANGE_LABEL'
    ),
  },
]);

const coexistenceSyncOptions = computed(() => [
  {
    key: 'contacts_only',
    value: 'contacts_only',
    label: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.SYNC.CONTACTS_ONLY_LABEL'
    ),
    help: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.SYNC.CONTACTS_ONLY_HELP'
    ),
  },
  {
    key: 'contacts_and_history',
    value: 'contacts_and_history',
    label: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.SYNC.CONTACTS_AND_HISTORY_LABEL'
    ),
    help: t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.SYNC.CONTACTS_AND_HISTORY_HELP'
    ),
  },
]);

const missingCoexistenceChecks = computed(() =>
  coexistenceChecks.value.filter(item => !item.enabled)
);

const coexistenceChecksReady = computed(
  () => missingCoexistenceChecks.value.length === 0
);

const isConnectDisabled = computed(
  () =>
    props.isDisabled ||
    isAuthenticating.value ||
    (isCoexistenceEnabled.value && !coexistenceChecksReady.value)
);

const hasRequiredBusinessData = (businessDataParam, eventType) => {
  if (!businessDataParam?.waba_id) {
    return false;
  }

  if (
    isCoexistenceEnabled.value ||
    eventType === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' ||
    eventType === 'FINISH_GRANT_ONLY_API_ACCESS'
  ) {
    return true;
  }

  return isValidBusinessData(businessDataParam);
};

// Error handling
const handleSignupError = error => {
  isProcessing.value = false;
  isAuthenticating.value = false;

  const errorMessage =
    parseAPIErrorResponse(error) ||
    error?.message ||
    t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
  useAlert(errorMessage);
};

const enableCallingForInbox = async inboxId => {
  try {
    await InboxesAPI.enableWhatsappCalling(inboxId);
  } catch (_) {
    useAlert(
      t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CALLING_ENABLE_FAILED')
    );
  }
};

const handleSignupSuccess = async inboxData => {
  if (inboxData && inboxData.id) {
    if (props.enableCallingOnComplete) {
      isProcessing.value = true;
      processingMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.ENABLING_CALLING'
      );
      await enableCallingForInbox(inboxData.id);
    }
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.FINISH.MESSAGE'));
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inboxData.id,
      },
    });
  } else {
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUCCESS_FALLBACK'));
    router.replace({
      name: 'settings_inbox_list',
    });
  }
};

const loadExistingNumbers = async () => {
  if (isLoadingExistingNumbers.value || isImportingExistingNumber.value) return;

  try {
    isLoadingExistingNumbers.value = true;
    const { data } = await WhatsappChannel.getExistingNumbers();
    existingNumbers.value = data.numbers || [];
  } catch (error) {
    handleSignupError(error);
  } finally {
    isLoadingExistingNumbers.value = false;
  }
};

const importExistingNumber = async ({ waba_id, phone_number_id }) => {
  if (isImportingExistingNumber.value) return;

  if (!waba_id || !phone_number_id) {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.REQUIRED'));
    return;
  }

  try {
    importingPhoneNumberId.value = phone_number_id;
    const { data } = await WhatsappChannel.importExistingNumber({
      waba_id,
      phone_number_id,
    });
    await handleSignupSuccess(data);
  } catch (error) {
    handleSignupError(error);
  } finally {
    importingPhoneNumberId.value = '';
  }
};

const importManualExistingNumber = () =>
  importExistingNumber({
    waba_id: manualWabaId.value.trim(),
    phone_number_id: manualPhoneNumberId.value.trim(),
  });

const completeSignupFlow = async (businessDataParam, eventType) => {
  if (!authCodeReceived.value || !authCode.value) {
    throw new Error(
      t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_NOT_COMPLETED')
    );
  }

  const requiresPhoneNumberSelection =
    eventType !== 'FINISH_ONLY_WABA' &&
    eventType !== 'FINISH_GRANT_ONLY_API_ACCESS';

  if (
    !hasRequiredBusinessData(businessDataParam, eventType) ||
    (requiresPhoneNumberSelection && !businessDataParam.phone_number_id)
  ) {
    throw new Error(
      t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA')
    );
  }

  isProcessing.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );

  const responseData = await store.dispatch(
    'inboxes/createWhatsAppOnboarding',
    {
      code: authCode.value,
      business_id: businessDataParam.business_id,
      waba_id: businessDataParam.waba_id,
      phone_number_id: businessDataParam.phone_number_id,
      onboarding_type: isCoexistenceEnabled.value ? 'coexistence' : 'standard',
      coexistence_sync_mode: isCoexistenceEnabled.value
        ? coexistenceSyncMode.value
        : undefined,
      flow_event: eventType,
      coexistence_debug_logs_enabled:
        isCoexistenceEnabled.value &&
        isSuperAdmin.value &&
        enableCoexistenceLogsNow.value,
    }
  );
  handleSignupSuccess(responseData);
};

const handleEmbeddedSignupData = async data => {
  if (!data || typeof data !== 'object') return;

  if (isEmbeddedSignupFinishEvent(data.event)) {
    const receivedBusinessData = data.data;
    businessData.value = receivedBusinessData;
    const flowEvent = data.event;
    businessFlowEvent.value = flowEvent;

    const requiresPhoneNumberSelection =
      flowEvent !== 'FINISH_ONLY_WABA' &&
      flowEvent !== 'FINISH_GRANT_ONLY_API_ACCESS';
    if (
      requiresPhoneNumberSelection &&
      !receivedBusinessData?.phone_number_id
    ) {
      isProcessing.value = false;
      isAuthenticating.value = false;
      useAlert(
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA')
      );
      return;
    }

    if (authCodeReceived.value && authCode.value) {
      await completeSignupFlow(receivedBusinessData, flowEvent);
    } else {
      processingMessage.value = t(
        'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_AUTH'
      );
    }
    return;
  }

  const embeddedSignupError = extractEmbeddedSignupError(data);
  if (embeddedSignupError?.message) {
    throw new Error(embeddedSignupError.message);
  }

  if (data.event === 'CANCEL') {
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    return;
  }

  if (data.event === 'error') {
    throw new Error(
      data.error_message ||
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR')
    );
  }
};

const handleSignupMessage = createMessageHandler(data => {
  handleEmbeddedSignupData(data).catch(handleSignupError);
});

const launchEmbeddedSignup = async () => {
  if (props.isDisabled) return;

  try {
    if (isCoexistenceEnabled.value && !coexistenceChecksReady.value) {
      useAlert(
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.MISSING_ERROR')
      );
      return;
    }

    if (!META_CONFIGURATION_ID) {
      throw new Error(
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA')
      );
    }

    isAuthenticating.value = true;
    processingMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_PROCESSING'
    );

    await setupFacebookSdk(META_APP_ID, META_API_VERSION);
    const code = await initWhatsAppEmbeddedSignup(META_CONFIGURATION_ID, {
      isCoexistenceEnabled: isCoexistenceEnabled.value,
      oauthRedirectUri: window.chatwootConfig?.whatsappOAuthRedirectUri,
    });

    authCode.value = code;
    authCodeReceived.value = true;
    processingMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_BUSINESS_INFO'
    );

    if (businessData.value) {
      await completeSignupFlow(businessData.value, businessFlowEvent.value);
    }
  } catch (error) {
    if (error.message === 'Login cancelled') {
      isProcessing.value = false;
      isAuthenticating.value = false;
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    } else {
      handleSignupError(error);
    }
  }
};

onMounted(() => {
  window.addEventListener('message', handleSignupMessage);
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleSignupMessage);
});
</script>

<template>
  <div class="h-full">
    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else>
      <div class="flex flex-col items-start mb-6 text-start">
        <div class="flex justify-start mb-6">
          <div
            class="flex size-11 items-center justify-center rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-woot-whatsapp" class="text-n-slate-10 size-6" />
          </div>
        </div>

        <h3 class="mb-2 text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.TITLE') }}
        </h3>
        <p class="text-sm leading-[24px] text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.DESC') }}
        </p>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <div
          v-for="benefit in benefits"
          :key="benefit.key"
          class="flex gap-2 items-center text-sm text-n-slate-11"
        >
          <Icon icon="i-lucide-check" class="text-n-slate-11 size-4" />
          {{ benefit.text }}
        </div>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <I18nT
          keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.TEXT"
          tag="span"
          class="text-sm text-n-slate-11"
        >
          <template #link>
            <a
              :href="globalConstants.WHATSAPP_EMBEDDED_SIGNUP_DOCS_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="underline text-n-brand"
            >
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.LINK_TEXT'
                )
              }}
            </a>
          </template>
        </I18nT>
      </div>

      <Banner v-if="showRestrictionAlert" color="amber" class="w-full mb-6">
        <div class="flex items-start gap-3 text-start">
          <Icon
            icon="i-lucide-triangle-alert"
            class="flex-shrink-0 size-4 mt-0.5"
          />
          <span>
            {{
              $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.RESTRICTED_WARNING')
            }}
            <a
              v-if="restrictionStatusUrl"
              :href="restrictionStatusUrl"
              class="link underline"
              rel="noopener noreferrer nofollow"
              target="_blank"
            >
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.STATUS_LINK') }}
            </a>
          </span>
        </div>
      </Banner>

      <div class="p-4 mb-6 rounded-xl border border-n-weak bg-n-background">
        <p class="mb-3 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.TITLE') }}
        </p>
        <label class="flex gap-3 items-start cursor-pointer">
          <input
            v-model="isCoexistenceEnabled"
            type="checkbox"
            class="mt-1 rounded border-n-weak text-n-brand focus:ring-n-brand"
          />
          <span class="text-sm text-n-slate-12">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.COE_EXISTENCE_LABEL'
              )
            }}
          </span>
        </label>
        <p class="mt-2 text-xs text-n-slate-11">
          {{
            $t(
              'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.COE_EXISTENCE_HELP'
            )
          }}
        </p>

        <div
          v-if="isCoexistenceEnabled"
          class="pt-3 mt-3 border-t border-n-weak"
        >
          <div class="p-3 mb-3 rounded-lg border border-n-weak bg-n-alpha-2">
            <p class="text-xs font-medium text-n-slate-12">
              {{
                $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.SYNC.TITLE')
              }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.SYNC.DESCRIPTION'
                )
              }}
            </p>
            <div class="flex flex-col gap-2 mt-3">
              <label
                v-for="option in coexistenceSyncOptions"
                :key="option.key"
                class="flex gap-3 items-start cursor-pointer"
              >
                <input
                  v-model="coexistenceSyncMode"
                  type="radio"
                  :value="option.value"
                  class="mt-1 border-n-weak text-n-brand focus:ring-n-brand"
                />
                <span class="text-sm text-n-slate-12">
                  {{ option.label }}
                  <span class="block mt-1 text-xs text-n-slate-11">
                    {{ option.help }}
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div class="p-3 rounded-lg border border-n-weak bg-n-alpha-2">
            <p class="text-xs font-medium text-n-slate-12">
              {{
                $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.TITLE')
              }}
            </p>
            <p class="mt-1 text-xs text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.DESCRIPTION'
                )
              }}
            </p>
            <div class="flex flex-col gap-2 mt-3">
              <label class="flex gap-3 items-start cursor-pointer">
                <input
                  v-model="acknowledgeHistoryWindow"
                  type="checkbox"
                  class="mt-1 rounded border-n-weak text-n-brand focus:ring-n-brand"
                />
                <span class="text-sm text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.HISTORY_WINDOW_LABEL'
                    )
                  }}
                </span>
              </label>
              <label class="flex gap-3 items-start cursor-pointer">
                <input
                  v-model="acknowledgeChunkedHistory"
                  type="checkbox"
                  class="mt-1 rounded border-n-weak text-n-brand focus:ring-n-brand"
                />
                <span class="text-sm text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.CHUNKED_HISTORY_LABEL'
                    )
                  }}
                </span>
              </label>
              <label class="flex gap-3 items-start cursor-pointer">
                <input
                  v-model="acknowledgeBsuidIdentity"
                  type="checkbox"
                  class="mt-1 rounded border-n-weak text-n-brand focus:ring-n-brand"
                />
                <span class="text-sm text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.BSUID_IDENTITY_LABEL'
                    )
                  }}
                </span>
              </label>
              <label class="flex gap-3 items-start cursor-pointer">
                <input
                  v-model="acknowledgeIdentityChangeFlow"
                  type="checkbox"
                  class="mt-1 rounded border-n-weak text-n-brand focus:ring-n-brand"
                />
                <span class="text-sm text-n-slate-12">
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.IDENTITY_CHANGE_LABEL'
                    )
                  }}
                </span>
              </label>
            </div>
            <p
              v-if="!coexistenceChecksReady"
              class="mt-3 text-xs font-medium text-n-ruby-9"
            >
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.CHECKS.MISSING_HINT'
                )
              }}
            </p>
          </div>

          <label class="flex gap-3 items-start cursor-pointer">
            <input
              v-model="deferAutoFillSetup"
              type="checkbox"
              class="mt-1 rounded border-n-weak text-n-brand focus:ring-n-brand"
            />
            <span class="text-sm text-n-slate-12">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.DEFER_AUTOFILL_LABEL'
                )
              }}
            </span>
          </label>
          <p class="mt-2 text-xs text-n-slate-11">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.DEFER_AUTOFILL_HELP'
              )
            }}
          </p>
          <div
            class="p-3 mt-3 rounded-lg border border-n-weak bg-n-alpha-2 text-xs text-n-slate-12"
          >
            <p class="font-medium">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.POST_SETUP.TITLE'
                )
              }}
            </p>
            <p class="mt-1">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.POST_SETUP.PROFILE'
                )
              }}
            </p>
          </div>

          <div v-if="isSuperAdmin" class="pt-3 mt-3 border-t border-n-weak">
            <label class="flex gap-3 items-start cursor-pointer">
              <input
                v-model="enableCoexistenceLogsNow"
                type="checkbox"
                class="mt-1 rounded border-n-weak text-n-brand focus:ring-n-brand"
              />
              <span class="text-sm text-n-slate-12">
                {{
                  $t(
                    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.COE_DEBUG_LOGS_LABEL'
                  )
                }}
              </span>
            </label>
            <p class="mt-2 text-xs text-n-slate-11">
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MODE.COE_DEBUG_LOGS_HELP'
                )
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex mt-4">
        <NextButton
          :disabled="isConnectDisabled"
          :is-loading="isAuthenticating"
          faded
          slate
          class="w-full"
          @click="launchEmbeddedSignup"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUBMIT_BUTTON') }}
        </NextButton>
      </div>

      <section v-if="isSuperAdmin" class="pt-6 mt-6 border-t border-n-weak">
        <div class="flex gap-3 justify-between items-start mb-4">
          <div>
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.TITLE') }}
            </h3>
            <p class="mt-1 text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.DESC') }}
            </p>
          </div>
          <NextButton
            icon="i-lucide-refresh-cw"
            faded
            slate
            :disabled="isImportingExistingNumber"
            :is-loading="isLoadingExistingNumbers"
            @click="loadExistingNumbers"
          />
        </div>

        <div v-if="existingNumbers.length" class="flex flex-col gap-2 mb-4">
          <div
            v-for="number in existingNumbers"
            :key="`${number.waba_id}-${number.phone_number_id}`"
            class="flex gap-3 justify-between items-center py-3 border-b border-n-weak"
          >
            <div class="min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ number.verified_name || number.waba_name }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ number.display_phone_number }} · {{ number.phone_number_id }}
              </p>
            </div>
            <NextButton
              :label="
                number.configured
                  ? $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.CONFIGURED'
                    )
                  : $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.IMPORT'
                    )
              "
              :disabled="number.configured || isImportingExistingNumber"
              :is-loading="importingPhoneNumberId === number.phone_number_id"
              faded
              slate
              @click="importExistingNumber(number)"
            />
          </div>
        </div>

        <div class="grid gap-3 sm:grid-cols-2">
          <label class="text-xs text-n-slate-11">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.WABA_ID') }}
            <input
              v-model="manualWabaId"
              type="text"
              class="mt-1 w-full"
              autocomplete="off"
              :disabled="isImportingExistingNumber"
            />
          </label>
          <label class="text-xs text-n-slate-11">
            {{
              $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.PHONE_ID')
            }}
            <input
              v-model="manualPhoneNumberId"
              type="text"
              class="mt-1 w-full"
              autocomplete="off"
              :disabled="isImportingExistingNumber"
            />
          </label>
        </div>
        <NextButton
          class="mt-3 w-full"
          icon="i-lucide-play"
          :label="
            $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.EXISTING.START_IMPORT')
          "
          :is-loading="importingPhoneNumberId === manualPhoneNumberId.trim()"
          :disabled="
            isImportingExistingNumber ||
            !manualWabaId.trim() ||
            !manualPhoneNumberId.trim()
          "
          blue
          @click="importManualExistingNumber"
        />
      </section>
    </div>
  </div>
</template>
